'use client'; import React from 'react';
import { motion } from 'framer-motion';
import WordByWordFade from './WordByWordFade';
import ScrollReveal from './ScrollReveal';
import { GraduationCap } from 'lucide-react';
import { SparklesIcon } from '@/components/ui/sparkles';
import { LayersIcon } from '@/components/ui/layers';
import { TrendingUpIcon } from '@/components/ui/trending-up';
import { cn } from '@/lib/utils';
import { analytics } from '../services/analytics';
const ASSET_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE_URL || '';
const avatar1 = `${ASSET_BASE}/assets/web/banner/avatar_1_small.webp`;
const avatar2 = `${ASSET_BASE}/assets/web/banner/avatar_2_small.webp`;
const avatar3 = `${ASSET_BASE}/assets/web/banner/avatar_3_small.webp`;
const avatar4 = `${ASSET_BASE}/assets/web/reviews/prof_1_small.webp`;

const features = [
    {
        title: 'AI-Powered Career Tools',
        description: 'Create resumes, practice interviews, and get instant AI feedback tailored to your goals.',
        icon: SparklesIcon,
        iconColor: 'text-blue-400',
        borderColor: 'border-blue-500/20',
        bgGradient: 'from-blue-500/80 to-transparent'
    },
    {
        title: 'All-in-One Platform',
        description: 'From job search & skill-building to mentorship and certifications everything in one place.',
        icon: LayersIcon,
        iconColor: 'text-purple-400',
        borderColor: 'border-purple-500/20',
        bgGradient: 'from-purple-500/80 to-transparent'
    },
    {
        title: 'Real Career Outcomes',
        description: 'Track your progress with data-driven insights and see measurable career growth.',
        icon: TrendingUpIcon,
        iconColor: 'text-teal-400',
        borderColor: 'border-teal-500/20',
        bgGradient: 'from-teal-500/80 to-transparent'
    },
    {
        title: 'Expert-Guided Learning',
        description: 'Learn directly from mentors who understand your goals and provide actionable advice.',
        icon: GraduationCap, // Static fallback
        iconColor: 'text-red-400',
        borderColor: 'border-red-500/20',
        bgGradient: 'from-red-500/80 to-transparent',
        hasBackground: true
    }
];

const WhyChooseUs = () => {
    return (
        <section className="py-24 bg-[#0B0F19] text-white relative overflow-hidden">
            {/* Background Grid Pattern - Fixed Light Grid for Dark Background */}
            <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.05]"
                style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
                    backgroundSize: '40px 40px'
                }}
            />

            <div className="container relative z-10">{/* 960px max-width */}

                {/* Header */}
                <div className="max-w-3xl mb-16 text-center lg:text-left mx-auto lg:mx-0">
                    <h2 className="text-brand-400 font-bold tracking-widest uppercase text-[10px] md:text-xs mb-3">
                        WHY CHOOSE US
                    </h2>
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight tracking-tight">
                        <WordByWordFade text="Built for Real Career Growth" />
                    </h3>
                    <p className="text-base md:text-lg text-gray-400 leading-relaxed max-w-2xl font-normal">
                        <WordByWordFade text="We make career growth simple. We combine AI and expert guidance to help you get hired, improve skills, and stay ahead all in one place." />
                    </p>
                </div>

                {/* Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
                    {features.map((feature, index) => (
                        <ScrollReveal
                            key={index}
                            direction={index % 2 === 0 ? 'left' : 'right'}
                            delay={index * 0.15}
                        >
                            <div
                                onClick={() => analytics.trackCardClick('Why Choose Us', feature.title)}
                                className={`
                                    relative p-8 rounded-2xl border border-white/10 bg-[#131926] hover:border-white/20 hover:-translate-y-2 transition-all duration-300 group overflow-hidden cursor-pointer shadow-sm hover:shadow-xl
                                `}
                            >
                                {/* Icon Container */}
                                <div className={`relative z-10 w-12 h-12 rounded-xl flex items-center justify-center border bg-white/5 mb-6 ${feature.borderColor} ${feature.iconColor} group-hover:scale-110 transition-transform duration-300`}>
                                    <feature.icon size={24} />
                                </div>

                                <h4 className="relative z-10 text-xl font-bold text-white mb-3 tracking-tight">
                                    {feature.title}
                                </h4>
                                <p className="relative z-10 text-gray-400 text-sm leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        </ScrollReveal>
                    ))}
                </div>

                {/* Bottom Floating Pill */}
                <div className="flex justify-center">
                    <div className="inline-flex items-center gap-4 px-2 pl-2 pr-6 py-2 rounded-full bg-[#1A202C] border border-white/10 shadow-lg backdrop-blur-md">
                        <div className="flex -space-x-3">
                            {[avatar1, avatar2, avatar3, avatar4].map((img, i) => (
                                <div key={i} className="w-10 h-10 rounded-full border-2 border-[#1A202C] overflow-hidden shadow-sm">
                                    <img src={img.src || img} alt="User" loading="lazy" className="w-full h-full object-cover" />
                                </div>
                            ))}
                        </div>
                        <span className="text-sm font-medium text-gray-300">
                            Join <span className="text-white font-semibold">100+ professionals</span> already on board
                        </span>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default WhyChooseUs;
